import { FC, useState } from 'react';
import { MenuPlacement } from 'react-select';
import { InputGroup, InputRightElement } from '@chakra-ui/react';

import * as Form from 'components/_core/form';
import IconButton from 'components/_core/IconButton';

import locales from './locales';

const VAT_VALUES = [0, 2.1, 5.5, 20];
type VatOption = number | 'other';

const formatPercent = (percentage: string) =>
  locales.formatString(locales.percentage, percentage) as string;

const VAT_OPTIONS = [
  { label: formatPercent('0'), value: 0 },
  { label: formatPercent('2.1'), value: 2.1 },
  { label: formatPercent('5.5'), value: 5.5 },
  { label: formatPercent('8.5'), value: 8.5 },
  { label: formatPercent('10'), value: 10 },
  { label: formatPercent('20'), value: 20 },
  { label: locales.other, value: 'other' },
] satisfies {
  label: string;
  value: VatOption;
}[];

interface VatInputProps {
  value: number;
  menuPlacement?: MenuPlacement;
  onChange: (value: string | number) => void;
  isDisabled?: boolean;
}

const VatInput: FC<VatInputProps> = ({
  isDisabled,
  menuPlacement = 'auto',
  onChange,
  value,
}) => {
  const [isCustomInput, setIsCustomInput] = useState(
    value && !VAT_VALUES.includes(value),
  );

  const onSelect: (vat: VatOption | null) => void = (vat) => {
    if (vat === null) {
      return;
    }

    if (vat === 'other') {
      setIsCustomInput(true);
    } else {
      onChange(vat);
    }
  };

  const onCloseCustomInput = () => {
    setIsCustomInput(false);
    onChange(0);
  };

  return isCustomInput ? (
    <InputGroup>
      <Form.Input
        isDisabled={isDisabled}
        onChange={(e) => onChange(e.target.value)}
        type="number"
        value={value}
      />

      {/*
        todo: implement clear button in Sunshine
        @see https://linear.app/shine/issue/SUN-28/add-clear-button-on-inputs
      */}
      <InputRightElement paddingRight="space-6">
        <IconButton
          aria-label=""
          icon="cancel"
          iconSize="icon-20"
          isDisabled={isDisabled}
          minWidth="inherit"
          onClick={onCloseCustomInput}
          paddingLeft="space-8"
          paddingRight="space-8"
          size="sm"
          variant="inline-secondary"
        />
      </InputRightElement>
    </InputGroup>
  ) : (
    <Form.SunshineSelect
      isDisabled={isDisabled}
      menuPlacement={menuPlacement}
      onChange={(option) => onSelect(option?.value ?? null)}
      options={VAT_OPTIONS}
      value={VAT_OPTIONS.find((option) => option.value === value)}
    />
  );
};

export default VatInput;
